import { Controller, useFormContext } from 'react-hook-form';
import { validateProps } from '../../../../../utils/propValidator';
import BaseCheckbox from './BaseCheckbox';

const propRules = {
  name: { type: 'string', required: true },
  value: {}, // required when used in array mode

  rules: { type: 'object' },
};

/**
 * ControlledCheckbox
 *
 * React Hook Form–controlled wrapper around BaseCheckbox.
 * Supports both boolean fields and checkbox-group (array) fields.
 *
 * Usage modes:
 * 1. Boolean mode:
 *    - Field value is a boolean.
 *    - `checked` reflects the boolean value directly.
 *
 * 2. Array mode (checkbox group):
 *    - Field value is an array.
 *    - `value` represents this checkbox's value in the array.
 *    - Checked state is derived from array inclusion.
 *
 * @param {Object} props
 * @param {string} props.name
 *   React Hook Form field name.
 * @param {*} [props.value]
 *   Value associated with this checkbox when used in array mode.
 *   Required when the form field value is an array.
 * @param {Object} [props.rules]
 *   React Hook Form validation rules.
 *
 * Passthrough props:
 * All other props are forwarded to BaseCheckbox.
 *
 * Notes:
 * - The form field must be initialized as an array to enable group behavior.
 * - Inline error rendering can be disabled via `showErrorInline={false}`
 *   and handled at the group or form level instead.
 *
 * @returns {React.ReactElement}
 */
export default function ControlledCheckbox(props: any) {
  validateProps(props as Record<string, unknown>, propRules, 'ControlledCheckbox');

  const { name, value, rules, ...rest } = props;
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const isArray = Array.isArray(field.value);

        const checked = isArray ? field.value?.includes(value) : Boolean(field.value);

        const handleChange = (next) => {
          if (!isArray) {
            field.onChange(next);
            return;
          }

          if (next) {
            field.onChange([...(field.value || []), value]);
          } else {
            field.onChange((field.value || []).filter((v) => v !== value));
          }
        };

        return (
          <BaseCheckbox
            {...rest}
            checked={checked}
            onChange={handleChange}
            error={Boolean(fieldState.error)}
            errorMessage={fieldState.error?.message}
          />
        );
      }}
    />
  );
}
